// =============================================================================
// ui/tabs/CoachDrill.tsx — the trades behind a leak.
// -----------------------------------------------------------------------------
// ── 🚨 THE NUMBER AND THE LIST ARE THE SAME POPULATION ──
//
// The row says "12 of 85 trades" and this opens the twelve. That is only worth
// anything if it IS the twelve — a list assembled from a second, similar-looking
// filter would disagree with the count sooner or later, and nothing on screen
// could explain the difference. So the list comes from the leak's own
// `contributors`, and a test pins it against the same leak's `dollarImpact().n`.
//
// ── ⚠ WHY THIS EXISTS AT ALL ──
//
// A dollar figure names a habit. It does not show you the habit. Before writing
// a rule meant to CATCH something, the trader has to see what they are catching:
// were the adds planned scale-ins or "it has to bounce" adds, were the ridden
// losers drifting sideways or genuinely building a case. The old client's own
// note: "click it to review the exact trades behind the leak and study what a
// rule would need to catch."
//
// ── ⚠ THE EXTRA COLUMN IS THE NUMBER THAT FLAGGED THE TRADE ──
//
// Not decoration. "Held vs 23m" beside a list of hold times makes the test
// self-evident; "Reason" turns three revenge re-entries and five FOMO chases
// into two different fixes. Which column, and what is in it, is the leak's own
// business — see `LeakReviewColumn`.
//
// ── ⚠ THE TRADE OPENS ON TOP, NOT INSTEAD ──
//
// The old client has ONE modal slot, so its drill closes, opens the trade, and
// re-opens itself from a callback when the trade closes. Here the trade detail
// renders over the list and closing it reveals the list underneath, unchanged —
// and the review set doubles as the ‹ › sibling list, so the trader can walk the
// twelve trades without returning to the table between each one.
// =============================================================================

import { useMemo, useState } from 'react';
import { TradeDetail } from './TradeDetail';
import { money, moneyPlain, num } from '../format';
import type { LeakReviewColumn } from '../../selectors/leaks';

/**
 * What the table draws. Every field is one the report's read already carries.
 *
 * ⚠ STRUCTURALLY TYPED, not an import of `LeakUniverseTrade`. The drill does not
 * care where the rows came from — only that they can be identified and printed —
 * and a component that named the io type would tie the table to one read.
 */
export interface DrillTrade {
  id: string;
  date: string;
  timeIn: string | null;
  contract: string | null;
  side: string | null;
  qty: number | null;
  entry: number | null;
  exit: number | null;
  rrRealized: number | null;
  mfePoints: number | null;
  maePoints: number | null;
  netPnl: number | null;
}

export interface CoachDrillProps {
  /** The leak's own name — the list is titled with it. */
  label: string;
  trades: readonly DrillTrade[];
  column: LeakReviewColumn | null;
  onClose: () => void;
  /** A save or delete inside the drill changes the leak — the report re-reads. */
  onChanged: () => void;
}

/**
 * Format the leak's own column.
 *
 * ⚠ THE UNIT COMES FROM THE LEAK, and every branch here is a different kind of
 * number. Printing 23 minutes and 23 dollars with one formatter is two facts
 * wearing one label — the same trap `rateText` avoids one screen up.
 *
 * ⚠ `moneyPlain`, NOT `money`, and the sign is decided here. `money` supplies its
 * own, so a negative run would print "−−$310" — PD-3, the double-sign bug, which
 * this session has already nearly re-created twice.
 */
function drillCell(col: LeakReviewColumn, t: DrillTrade): string {
  const v = col.value(t as never);
  if (v === null || v === undefined || v === '') return '—';
  if (col.unit === 'text') return String(v);
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (col.unit === 'usd') return `${n < 0 ? '−' : ''}${moneyPlain(Math.abs(n), 0)}`;
  if (col.unit === 'min') return `${num(n, 0)}m`;
  return `${num(n, 1)}R`;
}

export function CoachDrill(
  { label, trades, column, onClose, onChanged }: CoachDrillProps,
) {
  const [openId, setOpenId] = useState<string | null>(null);

  // ⚠ NEWEST FIRST, matching the old client. A behavioural review is read from
  // "what did I just do" backwards; oldest-first buries the trades the trader
  // still remembers under the ones they do not.
  const rows = useMemo(
    () => [...trades].sort((a, b) =>
      `${b.date}${b.timeIn ?? ''}`.localeCompare(`${a.date}${a.timeIn ?? ''}`)),
    [trades],
  );

  const ids = useMemo(() => rows.map((r) => r.id), [rows]);

  return (
    <>
      <div className="modal-scrim" role="dialog" aria-modal="true"
        aria-label={`${label} — the trades behind it`}>
        <div className="modal xwide">
          <header className="modal-head">
            <b>{label} · {num(rows.length, 0)} trade{rows.length === 1 ? '' : 's'}</b>
            <button type="button" className="btn" onClick={onClose}
              aria-label="Close">×</button>
          </header>

          <div className="modal-body">
            <div className="drill-wrap">
              <table className="drill-tbl">
                <thead>
                  <tr>
                    <th>Date</th><th>Time</th><th>Contract</th><th>Side</th>
                    <th className="numc">Qty</th>
                    <th className="numc">Entry</th>
                    <th className="numc">Exit</th>
                    {column ? <th className="numc">{column.header}</th> : null}
                    <th className="numc">R</th>
                    <th className="numc">MFE</th>
                    <th className="numc">MAE</th>
                    <th className="numc">Net P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className="clickable"
                      onClick={() => setOpenId(t.id)}>
                      <td>{t.date}</td>
                      <td>{t.timeIn ?? ''}</td>
                      <td>{t.contract ?? ''}</td>
                      <td>
                        <span className={t.side === 'short' ? 'red' : 'green'}>
                          {t.side === 'short' ? 'Short' : 'Long'}
                        </span>
                      </td>
                      <td className="numc">{t.qty ?? ''}</td>
                      <td className="numc">{t.entry === null ? '' : num(t.entry, 2)}</td>
                      <td className="numc">{t.exit === null ? '' : num(t.exit, 2)}</td>
                      {column ? (
                        <td className={`numc ${column.tone ?? ''}`}>{drillCell(column, t)}</td>
                      ) : null}
                      <td className="numc">
                        {t.rrRealized === null || !Number.isFinite(Number(t.rrRealized))
                          ? '—' : `${num(t.rrRealized, 2)}R`}
                      </td>
                      <td className="numc green">
                        {t.mfePoints === null ? '—' : num(Math.abs(t.mfePoints), 2)}
                      </td>
                      <td className="numc red">
                        {t.maePoints === null ? '—' : num(Math.abs(t.maePoints), 2)}
                      </td>
                      <td className={`numc ${(t.netPnl ?? 0) > 0 ? 'green' : (t.netPnl ?? 0) < 0 ? 'red' : ''}`}>
                        {money(t.netPnl ?? 0, 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted drill-hint">
              Click a row to open the full trade. The ‹ › there walk these{' '}
              {num(rows.length, 0)} and nothing else.
            </p>
          </div>

          <footer className="modal-foot">
            <span className="grow" />
            <button type="button" className="btn" onClick={onClose}>Close</button>
          </footer>
        </div>
      </div>

      {/* ⚠ RENDERED AFTER the list, so it paints over it — and closing it reveals
          the list still open, in the same scroll position. */}
      {openId ? (
        <TradeDetail
          tradeId={openId}
          siblingIds={ids}
          onNavigate={setOpenId}
          onSaved={() => { setOpenId(null); onChanged(); }}
          onDeleted={() => { setOpenId(null); onChanged(); }}
          onCancel={() => setOpenId(null)}
        />
      ) : null}
    </>
  );
}
